import moment, { Moment } from "moment";

export const formatCalendarDate = (dateString?: string) => {
  if (!dateString) return { month: "", day: "" };
  const m = moment(dateString, "YYYY-MM-DD");
  return {
    month: m.format("MMM").toUpperCase(),
    day: m.format("D"),
  };
};

/**
 * Parse a midnight-UTC calendar date from the backend (e.g. "2026-02-25T00:00:00Z")
 * into a local-midnight moment with the same Y-M-D.
 *
 * Backend `takenAt`-style fields represent a calendar date, not a moment in time.
 * Reading the Y-M-D in UTC keeps the date stable everywhere; rebuilding it in
 * local time means it can be compared against `localToday()` without cross-zone
 * drift (e.g. "yesterday" appearing for UTC− users while it's still today).
 */
export const parseCalendarDate = (dateString: string): Moment => {
  const utc = moment.utc(dateString);
  return moment([utc.year(), utc.month(), utc.date()]);
};

/**
 * Today as a local-midnight moment — the correct anchor for comparing against
 * calendar dates parsed with `parseCalendarDate`.
 */
export const localToday = (): Moment => moment().startOf("day");

/**
 * Whole calendar-day difference between two civil-date moments (`later` minus
 * `earlier`). DST-safe: subtracts Y-M-D parts only, ignoring any hour offset
 * that DST transitions would add to a millisecond-based diff.
 */
export const diffCalendarDays = (later: Moment, earlier: Moment): number => {
  const laterUtc = Date.UTC(later.year(), later.month(), later.date());
  const earlierUtc = Date.UTC(earlier.year(), earlier.month(), earlier.date());
  return Math.round((laterUtc - earlierUtc) / 86_400_000);
};
